import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { last } from 'lodash';
import type { Observable } from 'rxjs';
import {
  Subject,
  combineLatest,
  distinctUntilChanged,
  lastValueFrom,
  map,
  take,
} from 'rxjs';

import { assertValue } from '@libs/utils';

import {
  getCubicBezierControlPoint,
  getPointPositionOnQuadraticBezierCurve,
} from '../../helpers/bezierLine';
import type {
  Coordinate,
  Rectangle,
  RectangleAndZoom,
} from '../../helpers/geo';
import { putMapInBody } from '../../helpers/mapInstance';
import type {
  ClusterOptions,
  MapIntegrationType,
  Marker,
  MarkerListeners,
  MountedDynamicMap,
} from '../../maps';
import { DirectionsInstance } from '../googleDirections';
import type {
  ClusterMarkerFactory,
  GoogleMapsAddedMarker,
  SingleMarkerFactory,
} from '../types';
import { ConnectionType, getConnectionType } from '../../directions';
import type { RouteWithDetails } from '../../directions';
import { RouteCache } from '../../directions/routeCache';

import { googleMapElement } from './dynamicMapIntegration';
import {
  addAdvancedMarkerEventListeners,
  createGoogleMarkerClusterOptions,
} from './helpers';

const routeCache = RouteCache.getInstance();
export class MountedGoogleMap
  implements MountedDynamicMap<MapIntegrationType.GoogleMaps>
{
  private mapTypeId = new Subject<string>();
  private boundsChanged = new Subject<Date>();
  private idle = new Subject<Date>();
  private click = new Subject<Date>();
  private allRenderers: google.maps.DirectionsRenderer[] = [];
  private directionsService: google.maps.DirectionsService =
    DirectionsInstance.getInstance();
  private googleTravelModesByConnection = {
    [ConnectionType.NONE]: google.maps.TravelMode.WALKING,
    [ConnectionType.CAR]: google.maps.TravelMode.DRIVING,
    [ConnectionType.WALK]: google.maps.TravelMode.WALKING,
    [ConnectionType.BICYCLE]: google.maps.TravelMode.BICYCLING,
    //TODO: Change ConnectionType.FLIGHT withouth using google.maps.TravelMode
    [ConnectionType.FLIGHT]: google.maps.TravelMode.DRIVING,
    [ConnectionType.FERRY]: google.maps.TravelMode.DRIVING,
  };
  private existingPolylines: google.maps.Polyline[] = [];
  private singleMapMarkers: Set<GoogleMapsAddedMarker> = new Set();
  private markerClusterer: MarkerClusterer | undefined;
  private addMarkersBetweenRoutes = false;

  constructor(
    public googleMap: google.maps.Map,
    private singleMarkerFactory: SingleMarkerFactory,
    private clusterMarkerFactory: ClusterMarkerFactory
  ) {
    googleMap.addListener('bounds_changed', () =>
      this.boundsChanged.next(new Date())
    );

    googleMap.addListener('idle', () => this.idle.next(new Date()));

    googleMap.addListener('click', () => {
      this.click.next(new Date());
    });

    googleMap.addListener('maptypeid_changed', () => {
      if (googleMap.getMapTypeId() === undefined) {
        return;
      }
      const newMapTypeId = googleMap.getMapTypeId();
      if (!newMapTypeId) {
        return;
      }
      this.mapTypeId.next(newMapTypeId);
    });
  }

  public get viewChanged(): Observable<RectangleAndZoom> {
    return combineLatest([this.boundsChanged, this.idle]).pipe(
      distinctUntilChanged((prev, curr) => prev[1] === curr[1]),
      map(() => ({
        rectangle: this.getBoundingRectangle(),
        zoom: this.googleMap.getZoom(),
      }))
    );
  }
  public get mapTypeIdChanged(): Observable<string> {
    return this.mapTypeId.asObservable().pipe(distinctUntilChanged());
  }

  public updateAddMarkersBetweenRoutes(addMarkersBetweenRoutes: boolean): void {
    this.addMarkersBetweenRoutes = addMarkersBetweenRoutes;
  }

  public async showMarkers(markers: GoogleMapsAddedMarker[]): Promise<void> {
    if (markers.length === 0) {
      return Promise.resolve();
    }
    const bounds = markers.reduce(
      (current, x) => current.extend(x.position),
      new google.maps.LatLngBounds()
    );
    const minArea = 0.001;
    const neBoundCoordinates = bounds.getNorthEast().toJSON();
    const swBoundCoordinates = bounds.getSouthWest().toJSON();
    const area =
      (Math.pow(neBoundCoordinates.lng - swBoundCoordinates.lng, 2) +
        Math.pow(neBoundCoordinates.lat - swBoundCoordinates.lat, 2)) /
      2;
    if (area === 0) {
      const extra = 0.04;
      const neCoordinate = new google.maps.LatLng(
        neBoundCoordinates.lat + extra,
        neBoundCoordinates.lng + extra
      );
      const swCoordinate = new google.maps.LatLng(
        swBoundCoordinates.lat - extra,
        swBoundCoordinates.lng - extra
      );
      bounds.extend(neCoordinate).extend(swCoordinate);
    } else if (area < minArea && area > 0) {
      const x = neBoundCoordinates.lng - swBoundCoordinates.lng;
      const y = neBoundCoordinates.lat - swBoundCoordinates.lat;
      const r = x / y;
      const b = Math.sqrt(minArea / r);
      const a = r * b;
      const H = (a - x) / 2;
      const V = (b - y) / 2;
      const newNE = Object.assign({}, neBoundCoordinates);
      newNE.lat += V;
      newNE.lng += H;
      const newSW = Object.assign({}, swBoundCoordinates);
      newSW.lat -= V;
      newSW.lng -= H;
      const neCoordinate = new google.maps.LatLng(newNE.lat, newNE.lng);
      const swCoordinate = new google.maps.LatLng(newSW.lat, newSW.lng);
      bounds.extend(neCoordinate).extend(swCoordinate);
    }

    this.googleMap.fitBounds(bounds);
    await lastValueFrom(this.idle.pipe(take(1)));
  }

  public async center(rectangle: Rectangle): Promise<void> {
    this.googleMap.fitBounds(this.rectangleToLatLngBounds(rectangle));
    await lastValueFrom(this.idle.pipe(take(1)));
  }

  public clearRoutes(): void {
    this.allRenderers.forEach((renderer) => {
      renderer.setDirections({
        geocoded_waypoints: [],
        routes: [],
        request: {
          destination: '',
          origin: '',
          travelMode: google.maps.TravelMode.DRIVING,
        },
      });
    });
    this.allRenderers = [];
    this.existingPolylines.forEach((polyline) => polyline.setMap(null));
  }

  public getBoundingRectangle(): Rectangle {
    const bounds = this.googleMap.getBounds();
    if (!bounds) {
      console.warn('No bounds');
      return {
        topLeft: { latitude: 0, longitude: 0 },
        bottomRight: { latitude: 0, longitude: 0 },
      };
    }
    return this.latLngBoundsToRectangle(bounds);
  }

  public destroy(): void {
    this.clearRoutes();
    this.removeAllMarkers();
    if (!googleMapElement) {
      throw new Error("Don't find google Map Element to hide");
    }
    putMapInBody(googleMapElement);
    this.mapTypeId.complete();
  }

  public replaceMarker(
    existingMarker: GoogleMapsAddedMarker,
    newMarker: Marker,
    newMarkerListeners: MarkerListeners,
    newClusterOptions?: ClusterOptions
  ): GoogleMapsAddedMarker {
    this.removeMarker(existingMarker);

    const newAddedMarker = this.addMarker(
      newMarker,
      newMarkerListeners,
      newClusterOptions
    );

    return newAddedMarker;
  }

  public addMarker(
    marker: Marker,
    markerListeners?: MarkerListeners,
    clusterOptions?: ClusterOptions
  ): GoogleMapsAddedMarker {
    const position = new google.maps.LatLng(
      marker.position.latitude,
      marker.position.longitude
    );

    const singleMarker = this.singleMarkerFactory(marker, position);

    const addedGoogleMapsMarker: GoogleMapsAddedMarker = {
      marker: singleMarker,
      position,
      listeners: addAdvancedMarkerEventListeners(
        singleMarker,
        marker,
        markerListeners
      ),
      isClustered: !!marker.cluster,
    };

    if (marker.cluster) {
      if (!this.markerClusterer) {
        const markerClustererOptions = createGoogleMarkerClusterOptions(
          this.googleMap,
          marker,
          this.clusterMarkerFactory,
          clusterOptions
        );

        this.markerClusterer = new MarkerClusterer(markerClustererOptions);
      }

      this.markerClusterer.addMarker(singleMarker);
    } else {
      addedGoogleMapsMarker.marker.map = this.googleMap;
    }

    this.singleMapMarkers.add(addedGoogleMapsMarker);

    return addedGoogleMapsMarker;
  }

  public removeMarker(marker: GoogleMapsAddedMarker): void {
    if (marker.isClustered) {
      if (!this.markerClusterer) {
        throw new Error(
          'Trying to remove a marker without setting properly markerClusterer'
        );
      }

      this.markerClusterer.removeMarker(marker.marker);
    } else {
      marker.marker.map = null;
    }

    marker.listeners.forEach((listener) =>
      removeEventListener(listener.type, listener.callback)
    );

    this.singleMapMarkers.delete(marker);

    if (this.singleMapMarkers.size === 0) {
      this.markerClusterer = undefined;
    }
  }

  public addLine(startCoordinate: Coordinate, endCoordinate: Coordinate): void {
    const controlPointCoordinate = getCubicBezierControlPoint(
      startCoordinate,
      endCoordinate
    );
    const POLYLINE_SEGMENT_SIZE = 0.05;
    this.drawCurvedPolylineOnMap(
      startCoordinate,
      controlPointCoordinate,
      endCoordinate,
      POLYLINE_SEGMENT_SIZE,
      this.googleMap
    );
  }

  public addRoute(
    from: Marker,
    to: Marker,
    waypoints: Marker[]
  ): Promise<RouteWithDetails> {
    return new Promise<RouteWithDetails>((resolve) => {
      const directionsRenderer = this.initializeDirectionsRenderer();

      const request = this.getDirectionsRequest(from, to, waypoints);

      const segmentResult = routeCache.getRouteFromCache({
        from,
        to,
        waypoints,
      });

      const allPoints = [from, ...waypoints, to];
      if (
        segmentResult &&
        this.allRenderers.indexOf(directionsRenderer) !== -1
      ) {
        directionsRenderer.setDirections(segmentResult);
        if (this.addMarkersBetweenRoutes) {
          this.addCarMarkerAtMiddleOfRoute(segmentResult);
        }
        return resolve(
          this.getRoutesFromDirectionsResult(allPoints, segmentResult)
        );
      } else {
        this.directionsService.route(request, (result, status) => {
          if (
            status === 'OK' &&
            result &&
            this.allRenderers.indexOf(directionsRenderer) !== -1
          ) {
            routeCache.addRouteInCache({ from, to, waypoints }, result);

            directionsRenderer.setDirections(result);
            if (this.addMarkersBetweenRoutes) {
              this.addCarMarkerAtMiddleOfRoute(result);
            }
            const response = this.getRoutesFromDirectionsResult(
              allPoints,
              result
            );

            resolve(response);
          }
        });
      }
    });
  }

  private initializeDirectionsRenderer(): google.maps.DirectionsRenderer {
    const polylineOptions = {
      strokeColor: 'black',
      strokeWeight: 2,
      strokeOpacity: 1.0,
    };
    const rendererOptions = {
      markerOptions: {
        visible: false,
      },
      preserveViewport: true,
      map: this.googleMap,
      polylineOptions: this.addMarkersBetweenRoutes
        ? polylineOptions
        : undefined,
    };

    const directionsRenderer = new google.maps.DirectionsRenderer(
      rendererOptions
    );

    this.allRenderers.push(directionsRenderer);

    return directionsRenderer;
  }

  private getDirectionsRequest(
    from: Marker,
    to: Marker,
    waypoints: Marker[]
  ): google.maps.DirectionsRequest {
    const origin = new google.maps.LatLng(
      from.position.latitude,
      from.position.longitude
    );
    const destination = new google.maps.LatLng(
      to.position.latitude,
      to.position.longitude
    );

    const googleWaypoints = waypoints.map((waypoint) => ({
      location: new google.maps.LatLng(
        waypoint.position.latitude,
        waypoint.position.longitude
      ),
      stopover: true,
    }));
    const connection = getConnectionType(to);
    return {
      origin,
      destination,
      waypoints: googleWaypoints,
      travelMode: this.googleTravelModesByConnection[connection],
    };
  }

  private getRoutesFromDirectionsResult(
    markers: Marker[],
    result: google.maps.DirectionsResult
  ): RouteWithDetails {
    const route = result.routes[0];
    assertValue(
      route,
      'mountedDynamicMap.ts > getRoutesFromDirectionsResult > route '
    );

    return route.legs.map((leg, index) => {
      const distanceInMeters = leg.distance?.value;
      const timeInSeconds = leg.duration?.value;
      const metersInKilometer = 1000;
      const secondsInMinute = 60;

      const distanceInKilometers = distanceInMeters
        ? distanceInMeters / metersInKilometer
        : undefined;
      const expectedTravelTimeInMinutes = timeInSeconds
        ? timeInSeconds / secondsInMinute
        : undefined;

      const fromMarker = markers[index];
      const toMarker = markers[index + 1];

      assertValue(
        fromMarker,
        'mountedDynamicMap.ts > getRoutesFromDirectionsResult > fromMarker'
      );
      assertValue(
        toMarker,
        'mountedDynamicMap.ts > getRoutesFromDirectionsResult > toMarker'
      );

      const lastMarker = last(markers);
      assertValue(
        lastMarker,
        'mountedDynamicMap.ts > getRoutesFromDirectionsResult > lastMarker'
      );
      const connection = getConnectionType(lastMarker);

      return {
        distanceInKilometers,
        expectedTravelTimeInMinutes,
        type: connection,
        from: { ...fromMarker },
        to: { ...toMarker },
      };
    });
  }
  private async drawCurvedPolylineOnMap(
    startCoordinate: Coordinate,
    controlPointCoordinate: Coordinate,
    endCoordinate: Coordinate,
    segmentStepSize: number,
    map: google.maps.Map
  ): Promise<void> {
    const points: Coordinate[] = [];
    points.push(endCoordinate);
    for (
      let currentSegmentPoint = 0;
      currentSegmentPoint <= 1;
      currentSegmentPoint += segmentStepSize
    ) {
      points.push(
        getPointPositionOnQuadraticBezierCurve(
          startCoordinate,
          controlPointCoordinate,
          endCoordinate,
          currentSegmentPoint
        )
      );
    }
    const path: google.maps.LatLng[] = [];
    path.push(
      new google.maps.LatLng(
        startCoordinate.latitude,
        startCoordinate.longitude,
        false
      )
    );
    for (let i = 0; i < points.length - 1; i++) {
      const point = points[i + 1];
      assertValue(
        point,
        'mountedDynamicMap.ts > drawCurvedPolylineOnMap > point'
      );
      path.push(new google.maps.LatLng(point.latitude, point.longitude, false));
    }
    path.push(
      new google.maps.LatLng(
        endCoordinate.latitude,
        endCoordinate.longitude,
        false
      )
    );
    const mapType = this.googleMap.getMapTypeId() || 'roadmap';
    const strokeColor = mapType === 'hybrid' ? 'white' : 'black';
    const polyline = new google.maps.Polyline({
      path,
      geodesic: false,
      strokeOpacity: 1.0,
      strokeColor,
      strokeWeight: 2,
    });
    polyline.setMap(map);
    this.existingPolylines.push(polyline);
  }

  private removeAllMarkers(): void {
    this.singleMapMarkers.forEach((marker) => this.removeMarker(marker));
  }

  private rectangleToLatLngBounds(
    rectangle: Rectangle
  ): google.maps.LatLngBounds {
    return new google.maps.LatLngBounds(
      new google.maps.LatLng(
        rectangle.bottomRight.latitude,
        rectangle.topLeft.longitude
      ),
      new google.maps.LatLng(
        rectangle.topLeft.latitude,
        rectangle.bottomRight.longitude
      )
    );
  }

  private latLngBoundsToRectangle(bounds: google.maps.LatLngBounds): Rectangle {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    return {
      topLeft: {
        latitude: ne.lat(),
        longitude: sw.lng(),
      },
      bottomRight: {
        latitude: sw.lat(),
        longitude: ne.lng(),
      },
    };
  }

  private removeCarMarkers(): void {
    const markersToRemove: GoogleMapsAddedMarker[] = [];
    this.singleMapMarkers.forEach((marker) => {
      if (marker.marker.children[0]?.className === 'trip-summary-car-marker') {
        markersToRemove.push(marker);
      }
    });

    markersToRemove.forEach((marker) => {
      this.removeMarker(marker);
    });
  }

  private addCarMarkerAtMiddleOfRoute(
    result: google.maps.DirectionsResult
  ): void {
    this.removeCarMarkers();

    const route = result.routes[0];
    if (!route?.legs?.length) {
      return;
    }

    route.legs.forEach((leg, index) => {
      if (!leg?.steps?.length) {
        return;
      }

      const allPoints: google.maps.LatLng[] = [];
      leg.steps.forEach((step) => {
        if (step?.path?.length) {
          allPoints.push(...step.path);
        }
      });

      if (!allPoints.length) {
        return;
      }

      const middlePoint = allPoints[Math.floor(allPoints.length / 2)];
      if (!middlePoint) {
        return;
      }
      const carMarker: Marker = {
        key: `car-marker-${index}`,
        position: {
          latitude: middlePoint.lat(),
          longitude: middlePoint.lng(),
        },
        cssClass: 'car-marker',
        title: 'Car',
        cluster: false,
        useContentFormatter: 'car-marker',
      };

      this.addMarker(carMarker);
    });
  }
}
